export type OnlineBookingMode = "inquiry" | "reservation";

export const ONLINE_BOOKING_MODE_LABELS: Record<OnlineBookingMode, string> = {
  inquiry: "Anfragen",
  reservation: "Buchen",
};

export type OnlineBookingAddonBilling = "once" | "per_night" | "per_adult" | "per_person";

export const ONLINE_BOOKING_ADDON_BILLING_LABELS: Record<OnlineBookingAddonBilling, string> = {
  once: "Einmal pro Buchung",
  per_night: "Pro Nacht",
  per_adult: "Pro Erwachsenem",
  per_person: "Pro Person (Erwachsene + Kinder)",
};

export interface OnlineBookingFlowSettings {
  show_electricity_option: boolean;
  default_wants_electricity: boolean;
  show_dogs_field: boolean;
  show_children_field: boolean;
  show_companion_details: boolean;
  require_guest_address: boolean;
  show_traveler_age_field: boolean;
}

export const DEFAULT_ONLINE_BOOKING_FLOW: OnlineBookingFlowSettings = {
  show_electricity_option: true,
  default_wants_electricity: true,
  show_dogs_field: true,
  show_children_field: true,
  show_companion_details: true,
  require_guest_address: false,
  show_traveler_age_field: false,
};

export interface OnlineBookingAddonArticle {
  article_id: string;
  sort_order: number;
  billing: OnlineBookingAddonBilling;
  label: string | null;
  label_en?: string | null;
  article_number?: string | null;
  article_name?: string | null;
  unit?: string | null;
  is_active?: boolean | null;
}

export interface IntegrationApiKey {
  id: string;
  name: string;
  key_prefix: string;
}

export type CategorySortMode = "priority" | "price";
export type PriceComparisonReference = "hide" | "most_expensive" | "recommended" | "selected";
export type PriceComparisonBasis = "hide" | "per_night" | "total_stay";

export interface OnlineBookingUpsellingSettings {
  upselling_enabled: boolean;
  auto_highlight_recommended: boolean;
  auto_select_best_available: boolean;
  show_price_comparison: boolean;
  show_category_images: boolean;
  show_marketing_texts: boolean;
  show_badges: boolean;
  sort_mode: CategorySortMode;
  price_comparison_reference: PriceComparisonReference;
  price_comparison_basis: PriceComparisonBasis;
  template_upgrade: string;
  template_cheaper: string;
  template_total_difference: string;
  template_upgrade_en?: string | null;
  template_cheaper_en?: string | null;
  template_total_difference_en?: string | null;
  template_placeholders?: string[];
}

export const DEFAULT_ONLINE_BOOKING_UPSELLING: OnlineBookingUpsellingSettings = {
  upselling_enabled: true,
  auto_highlight_recommended: true,
  auto_select_best_available: true,
  show_price_comparison: true,
  show_category_images: true,
  show_marketing_texts: true,
  show_badges: true,
  sort_mode: "priority",
  price_comparison_reference: "recommended",
  price_comparison_basis: "per_night",
  template_upgrade: "Für nur {price_difference_per_night} € / Nacht mehr erhalten Sie {category_name}",
  template_cheaper:
    "{category_name} ist {price_difference_per_night} € / Nacht günstiger als {reference_category_name}",
  template_total_difference: "Für Ihren Aufenthalt nur {total_difference} € Unterschied",
};

export interface PitchCategoryMarketing {
  pitch_category_id: string;
  online_display_name: string | null;
  online_display_name_en?: string | null;
  teaser_text: string | null;
  teaser_text_en?: string | null;
  description_text: string | null;
  description_text_en?: string | null;
  priority: number;
  online_visible: boolean;
  highlight_recommended: boolean;
  badge_text: string | null;
  badge_text_en?: string | null;
  features: string[];
  features_en?: string[];
  hint_text: string | null;
  hint_text_en?: string | null;
  upselling_enabled: boolean;
  price_comparison_reference: PriceComparisonReference;
  price_comparison_basis: PriceComparisonBasis;
  images: { id?: string; sort_order: number; image_url: string; alt_text: string | null }[];
}

export interface PitchCategoryMarketingSuggestionProfile {
  id: string;
  label: string;
  online_display_name: string | null;
  online_display_name_en: string | null;
  teaser_text: string | null;
  teaser_text_en: string | null;
  description_text: string | null;
  description_text_en: string | null;
  badge_text: string | null;
  badge_text_en: string | null;
  features: string[];
  features_en: string[];
  hint_text: string | null;
  hint_text_en: string | null;
  highlight_recommended: boolean;
  priority: number;
}

export interface PitchCategoryMarketingSuggestions {
  category_name: string;
  category_name_en: string | null;
  recommended_profile_id: string;
  profiles: PitchCategoryMarketingSuggestionProfile[];
}

export interface OnlineBookingUpsellingTemplateSuggestions {
  template_upgrade: string;
  template_cheaper: string;
  template_total_difference: string;
  template_upgrade_en: string | null;
  template_cheaper_en: string | null;
  template_total_difference_en: string | null;
}

export type OnlineBookingSeasonMode = "fixed" | "recurring";

export interface OnlineBookingSeasonPeriod {
  id: string;
  name: string;
  mode: OnlineBookingSeasonMode;
  start_date: string | null;
  end_date: string | null;
  start_month: number | null;
  start_day: number | null;
  end_month: number | null;
  end_day: number | null;
}

export function newOnlineBookingSeason(
  mode: OnlineBookingSeasonMode = "recurring",
): OnlineBookingSeasonPeriod {
  return {
    id: crypto.randomUUID(),
    name: "",
    mode,
    start_date: null,
    end_date: null,
    start_month: mode === "recurring" ? 4 : null,
    start_day: mode === "recurring" ? 1 : null,
    end_month: mode === "recurring" ? 9 : null,
    end_day: mode === "recurring" ? 30 : null,
  };
}

export interface OnlineBookingMinStayPeriod {
  id: string;
  name: string;
  min_nights: number;
  mode: OnlineBookingSeasonMode;
  start_date: string | null;
  end_date: string | null;
  start_month: number | null;
  start_day: number | null;
  end_month: number | null;
  end_day: number | null;
}

export interface OnlineBookingMinStaySettings {
  enabled: boolean;
  default_min_nights: number;
  periods: OnlineBookingMinStayPeriod[];
}

export const DEFAULT_ONLINE_BOOKING_MIN_STAY: OnlineBookingMinStaySettings = {
  enabled: false,
  default_min_nights: 1,
  periods: [],
};

export interface OnlineBookingSameDayCutoffSettings {
  enabled: boolean;
  cutoff_time: string;
}

export const DEFAULT_ONLINE_BOOKING_SAME_DAY_CUTOFF: OnlineBookingSameDayCutoffSettings = {
  enabled: false,
  cutoff_time: "14:00",
};

export function newOnlineBookingMinStayPeriod(
  mode: OnlineBookingSeasonMode = "recurring",
): OnlineBookingMinStayPeriod {
  return {
    ...newOnlineBookingSeason(mode),
    min_nights: mode === "recurring" ? 7 : 3,
  };
}

export interface TouristIntegrationSettings {
  enabled: boolean;
  pms_import_email?: string | null;
  confirmation_email?: string | null;
  summary_email?: string | null;
  sold_out_nights?: string[];
  sold_out_message?: string;
}

export interface IntegrationsSettings {
  api_keys: IntegrationApiKey[];
  public_booking_enabled: boolean;
  online_booking_mode: OnlineBookingMode;
  online_booking_availability_check: boolean;
  auto_send_confirmation_email: boolean;
  pms_import_email?: string | null;
  google_places_api_key_configured?: boolean;
  google_places_api_key_masked?: string | null;
  tourist_integration?: TouristIntegrationSettings;
  companion_fields?: import("@/lib/companionFields").CompanionFieldsConfig;
  companion_fields_adult?: import("@/lib/companionFields").CompanionFieldsConfig;
  companion_fields_child?: import("@/lib/companionFields").CompanionFieldsConfig;
  primary_guest_fields?: import("@/lib/companionFields").CompanionFieldsConfig;
  online_booking_flow?: OnlineBookingFlowSettings;
  online_booking_addons?: OnlineBookingAddonArticle[];
  online_booking_upselling?: OnlineBookingUpsellingSettings;
  online_booking_seasons?: OnlineBookingSeasonPeriod[];
  online_booking_min_stay?: OnlineBookingMinStaySettings;
  online_booking_same_day_cutoff?: OnlineBookingSameDayCutoffSettings;
}
